import { FallbackDataService } from './fallbackDataService';
import { QueryClient } from '@tanstack/react-query';
import { getEnhancedMessageTrackingData } from '../services/outreach/mailgun/enhancedTracking';

export interface CacheWarmingConfig {
  criticalCampaigns: string[];
  prefetchOnStartup: boolean;
  backgroundRefreshInterval: number;
  maxConcurrentWarmups: number;
}

export class CacheWarmingService {
  private fallbackService: FallbackDataService;
  private config: CacheWarmingConfig;
  private warmupQueue: string[] = [];
  private activeWarmups = new Set<string>();
  private backgroundTimer: NodeJS.Timeout | null = null;

  constructor(queryClient: QueryClient, config: Partial<CacheWarmingConfig> = {}) {
    this.fallbackService = new FallbackDataService(queryClient);
    this.config = {
      criticalCampaigns: [],
      prefetchOnStartup: true,
      backgroundRefreshInterval: 5 * 60 * 1000, // 5 minutes
      maxConcurrentWarmups: 3,
      ...config,
    };
  }

  public async warmCriticalData(): Promise<void> {
    if (!this.config.prefetchOnStartup || this.config.criticalCampaigns.length === 0) {
      return;
    }

    console.info(`Warming cache for ${this.config.criticalCampaigns.length} critical campaigns`);

    const warmupPromises = this.config.criticalCampaigns.map(campaignId => 
      this.warmCampaignData(campaignId, 'high')
    );

    try {
      await Promise.allSettled(warmupPromises);
      console.info('Critical cache warming completed');
    } catch (error) {
      console.warn('Some cache warming operations failed:', error);
    }
  }

  public async warmCampaignData(
    campaignId: string, 
    priority: 'high' | 'medium' | 'low' = 'medium'
  ): Promise<void> {
    if (this.activeWarmups.has(campaignId)) {
      console.debug(`Campaign ${campaignId} already being warmed`);
      return;
    }

    if (this.activeWarmups.size >= this.config.maxConcurrentWarmups) {
      this.warmupQueue.push(campaignId);
      console.debug(`Campaign ${campaignId} queued for warming`);
      return;
    }

    this.activeWarmups.add(campaignId);

    try {
      await this.fallbackService.prefetchData(
        ['tracking', 'campaigns', campaignId],
        () => getEnhancedMessageTrackingData(campaignId),
        { priority, maxAge: 5 * 60 * 1000 } // 5 minutes
      );

      console.debug(`Successfully warmed cache for campaign ${campaignId}`);
    } catch (error) {
      console.warn(`Failed to warm cache for campaign ${campaignId}:`, error);
    } finally {
      this.activeWarmups.delete(campaignId);
      this.processWarmupQueue();
    }
  }

  public startBackgroundRefresh(): void {
    if (this.backgroundTimer) {
      return; // Already running
    }

    this.backgroundTimer = setInterval(() => {
      this.refreshStaleData();
    }, this.config.backgroundRefreshInterval);

    console.info('Background cache refresh started');
  }

  public stopBackgroundRefresh(): void {
    if (this.backgroundTimer) {
      clearInterval(this.backgroundTimer);
      this.backgroundTimer = null;
      console.info('Background cache refresh stopped');
    }
  }

  public addCriticalCampaign(campaignId: string): void {
    if (!this.config.criticalCampaigns.includes(campaignId)) {
      this.config.criticalCampaigns.push(campaignId);
      
      // Immediately warm this campaign if we're not at capacity
      if (this.activeWarmups.size < this.config.maxConcurrentWarmups) {
        this.warmCampaignData(campaignId, 'high');
      }
    }
  }

  public removeCriticalCampaign(campaignId: string): void {
    const index = this.config.criticalCampaigns.indexOf(campaignId);
    if (index > -1) {
      this.config.criticalCampaigns.splice(index, 1);
    }
  }

  public getCriticalCampaigns(): string[] {
    return [...this.config.criticalCampaigns];
  }

  public getWarmupStatus(): {
    activeWarmups: number;
    queuedWarmups: number;
    criticalCampaigns: number;
    backgroundRefreshActive: boolean;
  } {
    return {
      activeWarmups: this.activeWarmups.size,
      queuedWarmups: this.warmupQueue.length,
      criticalCampaigns: this.config.criticalCampaigns.length,
      backgroundRefreshActive: this.backgroundTimer !== null,
    };
  }

  private async refreshStaleData(): Promise<void> {
    const cacheStats = this.fallbackService.getCacheStats();
    
    if (cacheStats.staleEntries === 0) {
      return; // No stale data to refresh
    }

    console.debug(`Refreshing ${cacheStats.staleEntries} stale cache entries`);

    // Refresh critical campaigns first
    const refreshPromises = this.config.criticalCampaigns
      .slice(0, this.config.maxConcurrentWarmups)
      .map(campaignId => this.warmCampaignData(campaignId, 'medium'));

    try {
      await Promise.allSettled(refreshPromises);
    } catch (error) {
      console.warn('Background refresh encountered errors:', error);
    }
  }

  private processWarmupQueue(): void {
    if (this.warmupQueue.length === 0 || this.activeWarmups.size >= this.config.maxConcurrentWarmups) {
      return;
    }

    const nextCampaignId = this.warmupQueue.shift();
    if (nextCampaignId) {
      this.warmCampaignData(nextCampaignId, 'low');
    }
  }

  public cleanup(): void {
    this.stopBackgroundRefresh();
    this.warmupQueue = [];
    this.activeWarmups.clear();
  }
}

// Global cache warming service
let globalCacheWarmingService: CacheWarmingService | null = null;

export const initializeCacheWarming = (
  queryClient: QueryClient, 
  config?: Partial<CacheWarmingConfig>
): CacheWarmingService => {
  if (globalCacheWarmingService) {
    globalCacheWarmingService.cleanup();
  }

  globalCacheWarmingService = new CacheWarmingService(queryClient, config);
  return globalCacheWarmingService;
};

export const getCacheWarmingService = (): CacheWarmingService | null => {
  return globalCacheWarmingService;
};

// React hook for cache warming
export const useCacheWarmingService = () => {
  return globalCacheWarmingService;
};